import { useEffect, useState } from 'react';
import { Tabs, TabsContent, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Button } from '@shadcn/ui';
import { ServiceTabs } from 'app/components/service/ServiceTabs';
import { UTILITY_SERVICES } from 'app/utils/constants/utilityServices';
import { LightbulbIcon, Loader2, LockIcon, BoltIcon } from 'lucide-react';
import { NoDataFound } from 'app/components/project/NoDataFound';
import { useConEdisonData } from 'app/hooks/useConEdisonData';
import { ProjectInfoData } from 'app/services/project-services';
import useFeatureAccess from 'app/services/feature-access';
import { useNavigate } from 'react-router-dom';

interface ServiceLocation {
  serviceLocationId: string;
  meters: Array<{
    serialNumber: number;
    meterReadings: Array<{
      id: string;
      type: string;
      intervalDuration: string;
    }>;
  }>;
}

export interface CustomerMeter {
  customerAgreementId: string;
  serviceLocations: ServiceLocation[];
}

interface ConEdTabProps {
  project: ProjectInfoData | null;
}

export const ConEdTab = ({ project }: ConEdTabProps) => {
  const allServices = ['All'];
  const { customerMeters: conEdCustomerMeters, isLoading } = useConEdisonData(project);
  const { hasAccess } = useFeatureAccess();
  const navigate = useNavigate();

  const hasRealTimeAccess = hasAccess('realTimeMonitoring');

  if (!hasRealTimeAccess) {
    return (
      <div className="flex h-full flex-col items-center justify-center py-10">
        <div className="w-full max-w-lg rounded-lg border-2 border-border/30 bg-background/95 shadow-lg">
          <div className="bg-primary px-6 py-4 text-white">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/20">
                <BoltIcon className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-white/90">Premium Feature</h2>
            </div>
            <p className="mt-2 text-sm text-white/90">
              Real-time ConEdison data integration is available on the Business plan.
            </p>
          </div>

          <div className="px-8 py-10">
            <div className="flex flex-col items-center justify-center space-y-6 text-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                <LockIcon className="h-12 w-12 text-gray-400" />
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl font-medium text-gray-800">ConEdison Real-Time Data</h3>
                <p className="text-base text-gray-600">
                  Access real-time energy consumption data directly from ConEdison to monitor usage patterns, identify
                  efficiency opportunities, and make data-driven decisions for your building.
                </p>
                <div className="flex items-center justify-center space-x-2 pt-2">
                  <div className="rounded-full bg-primary/10 px-3 py-1">
                    <span className="text-sm font-medium text-primary">Usage Monitoring</span>
                  </div>
                  <div className="rounded-full bg-primary/10 px-3 py-1">
                    <span className="text-sm font-medium text-primary">Real-Time Data</span>
                  </div>
                  <div className="rounded-full bg-primary/10 px-3 py-1">
                    <span className="text-sm font-medium text-primary">Analytics</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/account/profile')}
                className="mt-4 w-[250px] bg-primary text-white hover:bg-primary/90"
                size="lg"
              >
                Upgrade to Business Plan
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[100%] items-center justify-center py-8">
        <Loader2 className="h-20 w-20 animate-spin text-primary" />
      </div>
    );
  }

  if (conEdCustomerMeters.length === 0) {
    return (
      <NoDataFound
        message="No ConEd data found"
        submessage="Please connect this project to a ConEd account to get started"
        icon={<LightbulbIcon className="h-8 w-8 text-muted-foreground" />}
      />
    );
  }

  return (
    <Tabs defaultValue="all" className="w-full">
      <ServiceTabs allServices={allServices} />

      {allServices.map((serviceType) => (
        <TabsContent key={serviceType} value={serviceType.toLowerCase()}>
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service Location</TableHead>
                  <TableHead>Meters</TableHead>
                  <TableHead>Available Readings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conEdCustomerMeters.flatMap((meter) =>
                  meter.serviceLocations.map((location) => (
                    <TableRow key={location.serviceLocationId}>
                      <TableCell className="font-medium">{location.serviceLocationId}</TableCell>
                      <TableCell>{location.meters.map((meter) => meter.serialNumber).join(', ')}</TableCell>
                      <TableCell>
                        {location.meters.map((meter) => (
                          <div key={meter.serialNumber} className="space-y-1">
                            {meter.meterReadings
                              .filter((reading) => reading.type !== 'Unknown')
                              .map((reading) => (
                                <div key={reading.id} className="text-sm">
                                  {reading.type} - {reading.intervalDuration}
                                </div>
                              ))}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};
